import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { dispatchMessageSave } from "../../store/reducers/messageReducer";
import { handleSubmit, fetchData, handleSubmitWithDispatch } from "../../auxiliar/auxFunctions";
import { getClinicOnlineBookingsData, putClinicOnlineBookings } from "../../actions/clinics";
import OnlineBookingsSkeleton from "../general/auxiliar/skeleton/OnlineBookingsSkeleton";

const OnlineBookings = () => {
    const dispatch = useDispatch();

    const [data, setData] = useState({
        waiting: true,
        online_booking: false,
        calendar_code: undefined,
    });
    const [copied, setCopied] = useState(false);
    const { id: clinicId } = useSelector((state) => state.activeClinic);

    useEffect(() => {
        fetchData(dispatch, getClinicOnlineBookingsData, clinicId, setData);
    }, []);

    const copyToClipboard = () => {
        if (data.waiting || !data?.calendar_code) {
            dispatchMessageSave(dispatch, {
                msg: "No se ha podido copiar el enlace, inténtalo de nuevo más tarde",
                type: "error",
            });

            return;
        }

        navigator.clipboard
            .writeText(
                `${process.env.REACT_APP_APP_URL}/reservation/agenda?clinic=${data?.calendar_code}`,
            )
            .then(() => {
                setCopied(true);
                dispatchMessageSave(dispatch, {
                    msg: "¡Enlace copiado al portapapeles! Ya puedes enviárselo a tus pacientes",
                    type: "info",
                });

                setTimeout(() => {
                    setCopied(false);
                }, 3000);
            })
            .catch(() => {
                dispatchMessageSave(dispatch, {
                    msg: "Inténtalo de nuevo, ha habido un error al copiar el enlace",
                    type: "error",
                });
            });
    };

    return (
        <section>
            <h2 className="sr-only">
                Reservas Online para clínicas de fisioterapia en MiFisio Agenda - Llega a más
                pacientes de la manera más fácil
            </h2>

            {data.waiting ? (
                <OnlineBookingsSkeleton />
            ) : (
                <div className="mb-8">
                    <h3 className="text-primary text-2xl">Reservas Online</h3>

                    <div>
                        <p className="flex items-center gap-3 mt-2 lg:text-lg">
                            <input
                                type="checkbox"
                                className="toggle toggle-primary"
                                checked={data?.online_booking}
                                onChange={handleSubmitWithDispatch(
                                    dispatch,
                                    putClinicOnlineBookings,
                                    clinicId,
                                    !data?.online_booking,
                                    setData,
                                )}
                            />
                            {data?.online_booking
                                ? "Las reservas online están activadas, para desactivarlas"
                                : "Las reservas online están desactivadas, para activarlas"}
                            , haz click en el botón de la izquierda
                        </p>

                        {data?.online_booking && (
                            <>
                                <p className="text-base lg:text-lg my-3">
                                    Puedes enviar el siguiente enlace a los pacientes para que hagan
                                    reservas online. Para copiarlo, haz click en el botón de abajo.
                                </p>

                                <div className="flex flex-col items-center">
                                    <button
                                        className={`btn ${copied ? "btn-primary" : "btn-secondary"}`}
                                        onClick={handleSubmit(copyToClipboard)}
                                    >
                                        {copied ? "¡Copiado!" : "Copiar enlace"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-base-200 shadow-xl w-full rounded-lg p-8 mt-3">
                <h3 className="text-secondary text-2xl">¿Cómo funcionan las reservas online?</h3>

                <p className="text-base lg:text-lg my-3">
                    Para habilitar las reservas online, activa la opción correspondiente en la parte
                    superior de esta sección. Esto permitirá que los pacientes consulten los
                    horarios disponibles y soliciten citas directamente desde la plataforma. A
                    continuación, asegúrate de configurar qué fisioterapeutas y tipos de sesiones
                    estarán disponibles para recibir reservas online. Esta configuración se realiza
                    desde las pantallas de cada fisioterapeuta y tipo de sesión específicos.
                </p>

                <p className="text-base lg:text-lg my-3">
                    Una vez activadas las reservas, puedes compartir el enlace con tus pacientes o
                    utilizarlo en tus campañas publicitarias. Ellos podrán elegir el profesional, la
                    fecha y la hora que mejor les convenga, viendo únicamente la información
                    necesaria: nombre del fisioterapeuta y sus horarios disponibles. La privacidad
                    de pacientes y profesionales está completamente protegida.
                </p>

                <p className="text-base lg:text-lg my-3">
                    Cuando un paciente solicita una cita, recibirás una notificación por correo
                    electrónico y la cita quedará registrada en el calendario de la clínica como
                    &quot;pendiente de confirmación&quot;. Una vez confirmes la reserva, esta se
                    actualizará automáticamente y el paciente recibirá un correo de confirmación con
                    todos los detalles.
                </p>

                <p className="text-base lg:text-lg my-3">
                    Si tienes cualquier duda, no dudes en escribirnos a{" "}
                    <a href="mailto:info@mifisioagenda.com" className="link">
                        info@mifisioagenda.com
                    </a>
                    . Estamos aquí para ayudarte.
                </p>
            </div>
        </section>
    );
};

export default OnlineBookings;
